import React from 'react';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router';
import { FiArrowRight } from 'react-icons/fi';
import { FiDownload } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { ourSolutionData } from '../../utils/ourSolutionData';
import DownloadBrochureModal from '../DownloadBrochureModal/DownloadBrochureModal';

const ArrowIcon = FiArrowRight as React.ComponentType<{ className?: string }>;
const DownloadIcon = FiDownload as React.ComponentType<{ className?: string }>;

const OurSolution: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [selectedItem, setSelectedItem] = React.useState<(typeof ourSolutionData)[number] | null>(null);

  const openBrochureModal = (item: (typeof ourSolutionData)[number]) => {
    setSelectedItem(item);
    setIsModalOpen(true);
  };

  const closeBrochureModal = () => {
    setIsModalOpen(false);
  };

  return (
    <section className="py-28 bg-white relative overflow-hidden">
      <div className="relative max-w-7xl mx-auto px-6">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-16">
          <div>
            <p className="text-teal-600 text-xs font-bold uppercase tracking-[0.2em] mb-3">
              {t('ourSolutionSection.eyebrow', { defaultValue: 'Our Solutions' })}
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-bold text-[#0F172A] tracking-tight">
              {t('ourSolutionSection.titleLine1', { defaultValue: 'End-to-End Technology' })}
              <br className="hidden sm:block" />
              {t('ourSolutionSection.titleLine2', { defaultValue: 'Solutions Under One Roof' })}
            </h2>
          </div>
          <Link
            to="/solutions"
            className="inline-flex items-center gap-2 text-teal-600 font-semibold text-sm hover:gap-3 transition-all shrink-0"
          >
            {t('ourSolutionSection.viewAll', { defaultValue: 'View all solutions' })}
            <span aria-hidden="true" className="inline-flex items-center">
              <ArrowIcon className="h-4 w-4" />
            </span>
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {ourSolutionData.map((item) => (
            <article
              key={item.id}
              className="group cursor-pointer relative bg-[#F7F8FA] rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-500 hover:-translate-y-1"
              onClick={() => navigate(`/solution#${item.slug}`)}
            >
              <div className="relative h-52 overflow-hidden">
                {item.video ? (
                  <video
                    src={item.video}
                    poster={item.image}
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="metadata"
                    aria-label={item.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                ) : (
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                )}
              </div>
              <div className="p-6">
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#94A3B8] mb-2">
                  {t(`ourSolutionSection.items.${item.slug}.tag`, { defaultValue: item.tag })}
                </p>
                <h3 className="text-lg font-bold text-[#0F172A] tracking-tight">
                  {t(`ourSolutionSection.items.${item.slug}.title`, { defaultValue: item.title })}
                </h3>
                <p
                  className="text-[#64748B] text-sm mt-2 leading-relaxed overflow-hidden text-ellipsis"
                  style={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                  }}
                >
                  {t(`ourSolutionSection.items.${item.slug}.description`, { defaultValue: item.description })}
                </p>
                <div className="mt-4 flex items-center justify-between">
                  <Link
                    to={`/solution#${item.slug}`}
                    className="inline-flex items-center gap-1.5 text-teal-600 text-sm font-semibold group-hover:gap-2.5 transition-all"
                  >
                    {t('ourSolutionSection.learnMore', { defaultValue: 'Learn more' })}
                    <span aria-hidden="true" className="inline-flex items-center">
                      <ArrowIcon className="h-4 w-4" />
                    </span>
                  </Link>
                  <button
                    type="button"
                    aria-label="Download"
                    className="group/download relative inline-flex h-10 w-10 items-center justify-center rounded-full bg-white text-teal-600 shadow-sm ring-1 ring-[#E2E8F0] transition-all hover:bg-teal-50 hover:shadow"
                    onClick={(e) => {
                      e.stopPropagation();
                      openBrochureModal(item);
                    }}
                  >
                    <DownloadIcon className="h-4 w-4" />
                    <span className="pointer-events-none absolute -top-10 right-0 z-10 whitespace-nowrap rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-lg opacity-0 transition-all duration-200 group-hover/download:-translate-y-0.5 group-hover/download:opacity-100">
                      {t('ourSolutionSection.downloadBrochure', { defaultValue: 'Download brochure' })}
                      <span className="absolute -bottom-1 right-3 h-2 w-2 rotate-45 border-b border-r border-slate-200 bg-white"></span>
                    </span>
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
      <DownloadBrochureModal
        isOpen={isModalOpen && !!selectedItem}
        onClose={closeBrochureModal}
        solutionSlug={selectedItem?.slug || ''}
        solutionTitle={selectedItem?.title || ''}
        solutionDescription={selectedItem?.description || ''}
        brochurePdf={selectedItem?.brochurePdf || ''}
      />
    </section>
  );
};

export default OurSolution;
